import React, { Fragment, useState, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Trans } from '@lingui/macro'
import queryString from 'query-string'

import Logo from '../../components/Logo'
import Row from '../../components/Row'
import Toast from '../../components/Toast'
import { Typography, Button, Column, Container, TextInput } from 'components'
import * as routes from '../../settings/_routesSettings'
import * as actions from '../../redux/actions'
import { history } from '../../routers/AppRouter'

const schema = yup
  .object()
  .shape({
    username: yup.string()
      .email('O e-mail é inválido')
      .required('O campo é obrigatório'),
    password: yup.string()
      .required('O campo é obrigatório')
      .min(6, 'A senha é muito curta')
      .max(20, 'A senha é muito longa')
  })
  .required()

const Login = ({ ...props }) => {
  const { register, handleSubmit, watch, formState: { errors, isValid } } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const usernameValue = watch('username')
  const passwordValue = watch('password')

  const { ref: usernameRef, ...usernameRest } = register('username', { required: true, pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i })
  const { ref: passwordRef, ...passwordRest } = register('password', { required: true })

  const [loading, setLoading] = useState(false)
  const [redirectTo, setRedirectTo] = useState(null)
  const [isAutoFill, setIsAutoFill] = useState(false)

  const { location, userToken, isAuth, setGenericError } = props

  useEffect(() => {
    document.title = 'Entrar - Telavita'

    const getQuery = queryString.parse(location.search)
    setRedirectTo(getQuery.redirectTo)

    if (getQuery.sessionExpired !== undefined)
      setGenericError('Sua sessão expirou. Insira novamente seu e-mail e senha.')
      
    if (userToken && isAuth) history.push(routes.DASHBOARD)
  }, [ location, userToken, isAuth, setGenericError ])

  const onSubmit = async(data) => {
    setLoading(true)

    const authData = {
      username: data.username,
      password: data.password,
    }

    await props.handleLogin(authData, redirectTo)
    setLoading(false)
  }

  useEffect(() => {
    const usernameInput = document.getElementById('username')
    const passwordInput = document.getElementById('password')
    
    const timeout = setTimeout(() => {
      if (usernameInput.matches(':-webkit-autofill') && passwordInput.matches(':-webkit-autofill')) {
        setIsAutoFill(true)
      }
    }, 300)

    return () => clearTimeout(timeout)
    
  },[])

  useEffect(() => {
    if (isAutoFill) setIsAutoFill(false)
  },[usernameValue, passwordValue])

  return (
    <Fragment>
      <Container>
        <Row justify='center' className="Login__Header">
          <Column align='center' justify='center' colWidth={4}>
            <Column style={{ textAlign: 'center' }}>
              <div>
                <Logo fill="#1f35b5" onClickLogo={() => window.location.href = process.env.REACT_APP_INSTITUTIONAL_URL} />
              </div>
            </Column>
          </Column>
        </Row>

        <Row justify='center'>
          <Column colWidth={4}>
            <Typography Tag="h1" type="custom" align="center" style={{ fontSize: '30px' }}>
              <Trans> Entrar </Trans>
            </Typography>

            <Row justify='center' margin='30px 0 0'>
              <form className="hasMarginBottom" onSubmit={handleSubmit(onSubmit)} style={{ marginBottom: '25px' }}>
                {
                  props.failMessage !== '' ?
                    <Row key={props.failMessage} margin="0 0 2rem 0">
                      <Column>
                        <Toast variant="error" icon='Info' message={props.failMessage} />
                      </Column>
                    </Row> : null
                }

                <Row>
                  <Column>
                    <TextInput
                      autofocus={true}
                      disabled={false}
                      hasError={errors?.username}
                      messageError={errors.username?.message ?? ''}
                      name="username"
                      placeholder="E-mail"
                      required={true}
                      type="email"
                      data-cy="login-username"
                      inputRef={usernameRef}
                      {...usernameRest}
                    />
                  </Column>
                </Row>

                <Row margin='0 0'>
                  <Column>
                    <TextInput
                      autofocus={false}
                      disabled={false}
                      hasError={errors?.password}
                      messageError={errors.password?.message ?? ''}
                      isPassword
                      name="password"
                      placeholder="Senha"
                      required={true}
                      autocapitalize="off" 
                      autocorrect="off"
                      data-cy="login-password"
                      inputRef={passwordRef}
                      {...passwordRest}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <Button
                      loading={loading}
                      disabled={!isAutoFill && !isValid}
                      text={(<Trans>ENTRAR</Trans>)}
                      type="submit"
                      data-cy="login-submit"
                    />
                  </Column>
                </Row>
              </form>

              <Row direction="row">
                <Column className="Login__Content__Column">
                  <Typography type="custom" style={{ fontWeight: '500', lineHeight: '18px' }}>
                    <Link to={routes.RECOVER_EMAIL} className="Login__Content__Link">
                      <Trans> Esqueceu seu e-mail? </Trans>
                    </Link>
                  </Typography>

                  <Typography style={{ marginTop: '10px', fontWeight: '500', lineHeight: '18px' }}>
                    <Link to={routes.FORGOT_PASSWORD} className="Login__Content__Link">
                      <Trans> Esqueceu a senha? </Trans>
                    </Link>
                  </Typography>
                </Column>

                <Column className="Login__Content__Column">
                  <Typography align="right">
                    <Trans>Não tem conta?</Trans>
                  </Typography>

                  <Typography align="right" type="custom" style={{ fontWeight: '500', lineHeight: '18px' }}>
                    <Link to={routes.REGISTER} className="Login__Content__Link">
                      <Trans>Crie a sua</Trans>
                    </Link>
                  </Typography>
                </Column>
              </Row>
            </Row>
          </Column>
        </Row>
      </Container>
    </Fragment>
  )
}

const mapStateToProps = state => ({ isAuth: state.auth.isAuth, failMessage: state.auth.failMessage })

const mapDispatchToProps = dispatch => ({
  handleLogin: (authData, redirectTo) => dispatch(actions.authLogin(authData, redirectTo)),
  setGenericError: (message) => dispatch(actions.setGenericError(message)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Login)